import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import * as ViewActions from '../actions/Actions'
import { css, StyleSheet } from 'lib/paypalphrodite'
import { trackImpression } from '../../lib/analytics/fpti'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import content from 'pp-react-l10n'
import { getExternalComponents } from '../hooks'
import serverProps from '../utils/serverProps'
import PPAmountFieldV2BigFont from 'p2p-amount-field/dist/p2p-amount-field.basic.v2-big-font.umd'
import PPAmountFieldV4 from 'p2p-amount-field/dist/p2p-amount-field.basic.v4.umd'
import { isDw2Repaint } from 'experiments/dw2Repaint'
import { showCurrencySymbol } from 'transfer/utils/currencyUtils'
import { getFlowType } from 'transfer/utils/url-utils'
import { ActionTypes as types } from '../constants'
import FFFInfoMessage from './first-fee-free/info-message'

let i18nPreview = content('transfer/page/preview')

const { env = '' } = serverProps

let i18n = content('transfer/inc/amount')

const styles = StyleSheet.create({
  firstTimeUserHelpInfo: {
    textAlign: 'left',
    transition: 'all 0.25s ease-in-out',
    maxHeight: '0px',
    opacity: '0',
    overflow: 'hidden',
    marginBottom: '1em',
  },
  firstTimeUserHelpInfoEnter: {
    maxHeight: '250px',
    opacity: '1',
  },
})

class SendAmountField extends React.PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    amount: PropTypes.string.isRequired,
    payRequestAmount: PropTypes.any,
    flowIsPayRequest: PropTypes.bool,
    currencyCode: PropTypes.string.isRequired,
    currencyCodeList: PropTypes.array.isRequired,
    currencyDecimal: PropTypes.string.isRequired,
    currencyMap: PropTypes.object.isRequired,
    showInvalid: PropTypes.bool,
    sendingLimit: PropTypes.string.isRequired,
    isAmountReadOnly: PropTypes.bool,
    isCurrencyCodeReadOnly: PropTypes.bool.isRequired,
    showCondensedUI: PropTypes.bool,
    onFocus: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    getExternalComponents: PropTypes.func,
    shouldFocusAmountField: PropTypes.bool,
    isReview: PropTypes.bool,
    isXB: PropTypes.bool,
    onChange: PropTypes.func,
    disableAmountField: PropTypes.bool,
    isMgm: PropTypes.bool,
    isDonate: PropTypes.bool,
    isFirstFeeFreeEligible: PropTypes.bool,
    firstFeeFreeThresholdAmount: PropTypes.object,
    showFFFInfoMessage: PropTypes.bool,
    enableFFFCapLimit: PropTypes.bool,
    toCountryCode: PropTypes.string,
  }

  state = {
    showFirstTimeUserHelpInfo: false,
    shouldFocusAmountField: false,
  }

  // need to check the currencySymbol here, otherwise, amount component will re-render when user type anyting in note field
  componentDidMount() {
    // Show the appropriate currency for pay request
    this.shouldshowCurrencySymbol = showCurrencySymbol(
      this.props.amount,
      this.props.currencyCode,
      this.props.currencyDecimal
    )
  }

  handleChange = (amount, currencyCode) => {
    if (this.props.disableAmountField) {
      return
    }

    if (
      this.props.isFirstFeeFreeEligible &&
      this.props.enableFFFCapLimit &&
      this.props.toCountryCode === 'US'
    ) {
      if (
        this.props.showFFFInfoMessage &&
        Number(amount) <= Number(this.props.firstFeeFreeThresholdAmount.value)
      ) {
        this.props.dispatch(ViewActions.toggleFFFInfoMessage(false))
      }

      if (
        !this.props.showFFFInfoMessage &&
        Number(amount) > Number(this.props.firstFeeFreeThresholdAmount.value)
      ) {
        this.props.dispatch(ViewActions.toggleFFFInfoMessage(true))
      }
    }

    if (this.props.amount !== amount && !this.amountAlreadyTyping) {
      this.amountAlreadyTyping = true
      tracking(Events.p2p_amount_typing_started({}))
    }
    this.props.dispatch(ViewActions.changeAmount(amount))

    // Don't show the currency exchange calculator for pay request
    if (
      !this.props.flowIsPayRequest &&
      currencyCode !== this.props.currencyCode &&
      !this.props.isXB
    ) {
      this.props.dispatch(ViewActions.initFXCalculator(amount, currencyCode))
    }

    // dispatch changeCurrency after the check above is because otherwise currencyCode will
    // be equal to this.props.currencyCode
    this.props.dispatch(ViewActions.changeCurrency(currencyCode))

    if (this.props.onChange) {
      this.props.onChange({ amount, currencyCode })
    }
  }

  handleFocus = amount => {
    // if amount field is read only and we are on the review page
    // add an option for a user to change the amount by clicking on the amount field
    if (
      this.props.isAmountReadOnly &&
      this.props.isReview &&
      !this.props.flowIsPayRequest
    ) {
      this.props.dispatch(ViewActions.changeAmount(amount))
    }

    this.props.dispatch({
      type: types.EXTERNAL.HIDE_AMOUNT_DONATION_ERROR,
    })
  }

  // This object is part of the instance to fix and edge case where PPAmountField
  // was stealing focus from the unilateral recipient field on every keystroke.
  inputProps = { onFocus: e => this.handleFocus(e.target.value) }

  handleFirstTimeUserTooltipClick = () => {
    this.setState(
      {
        showFirstTimeUserHelpInfo: !this.state.showFirstTimeUserHelpInfo,
        shouldFocusAmountField: true,
      },
      () => {
        this.setState({ shouldFocusAmountField: false })

        const display = this.state.showFirstTimeUserHelpInfo ? 'open' : 'close'
        const pageName = `main:consumer:p2p:send:amountField:${display}`
        trackImpression(pageName, pageName)
      }
    )
  }

  getFirstTimeUserHelpContentKey = () => {
    const numCurrencies = Object.keys(this.props.currencyMap).length
    if (numCurrencies < 2) {
      return 'label.newUserSendMoneyInfo'
    }

    if (this.props.sendingLimit) {
      return 'label.newUserSendMoneyLimitInfo'
    }

    return 'label.newUserSendMoneyNoLimitInfo'
  }

  shouldFocusOnAmountField = () => {
    if (env === 'donate') return false

    if (this.props.shouldFocusAmountField !== undefined) {
      return this.props.shouldFocusAmountField
    }
    return this.state.shouldFocusAmountField
  }

  render() {
    const firstTimeUserHelpContent = i18n.html(
      this.getFirstTimeUserHelpContentKey(),
      {
        currencyLength: Object.keys(this.props.currencyMap).length,
        sendingLimit: this.props.sendingLimit,
      }
    )

    const firstTimeUserHelpContentWrapperClasses = css(
      styles.firstTimeUserHelpInfo,
      this.state.showFirstTimeUserHelpInfo && styles.firstTimeUserHelpInfoEnter
    )

    const PPAmountField = isDw2Repaint()
      ? PPAmountFieldV4
      : PPAmountFieldV2BigFont

    return (
      <div>
        <PPAmountField
          name="amount"
          id="fn-amount"
          dataNemo="amount"
          ariaData={i18nPreview('label.enterAmount')}
          isAmountReadOnly={this.props.isAmountReadOnly}
          isCurrencyCodeReadOnly={
            this.props.isMgm || this.props.isCurrencyCodeReadOnly
          }
          currencyDecimal={this.props.currencyDecimal}
          currencyCodeList={this.props.currencyCodeList}
          currencySymbolMap={this.props.currencyMap}
          currencyCode={this.props.currencyCode}
          value={this.props.amount}
          onChange={this.handleChange}
          focusAmountField={this.shouldFocusOnAmountField()}
          showCurrencySymbol={this.shouldshowCurrencySymbol}
          inputProps={this.inputProps}
        />
        {this.props.getExternalComponents('before:firstTimeHelp')}
        {!isDw2Repaint() && (
          <div className={firstTimeUserHelpContentWrapperClasses}>
            <div className="vx_alert vx_alert-help">
              <p className="vx_alert-text">{firstTimeUserHelpContent}</p>
            </div>
          </div>
        )}

        {this.props.showFFFInfoMessage && <FFFInfoMessage />}

        {!this.props.isDonate &&
          this.props.getExternalComponents('after:firstTimeHelp')}
      </div>
    )
  }
}

export default connect(
  (
    { server, amount, external, transaction, crossBorder },
    { isAmountReadOnly, isCurrencyCodeReadOnly }
  ) => ({
    amount: server.payRequestAmount || amount.amount,
    currencyCode: server.payRequestCurrencyCode || amount.currencyCode,
    currencyCodeList: amount.currencyCodeList,
    currencyDecimal: server.payRequestCurrencyDecimal || amount.currencyDecimal,
    currencyMap: amount.currencyMap,
    flowIsPayRequest: getFlowType() === 'payRequest',
    isXB: server.env === 'xb',
    sendingLimit: server.sendingLimit,
    isAmountReadOnly:
      !!external.isAmountReadOnly ||
      !!(server.env === 'pisp' && !external.amountEditable) ||
      isAmountReadOnly,
    isCurrencyCodeReadOnly:
      !!external.isCurrencyCodeReadOnly ||
      !!(server.env === 'pisp' && !external.currencyCodeEditable) ||
      isCurrencyCodeReadOnly,
    showCondensedUI: !!external.showCondensedUI, // force to boolean and remove extra margin, etc.
    getExternalComponents: getExternalComponents(
      server.env,
      'send-amount-field'
    ),
    isMgm: server.isMgm,
    isDonate: server.env === 'donate',
    isFirstFeeFreeEligible: transaction.isFirstFeeFreeEligible,
    firstFeeFreeThresholdAmount: transaction.firstFeeFreeThresholdAmount,
    showFFFInfoMessage: transaction.showFFFInfoMessage,
    enableFFFCapLimit: transaction.enableFFFCapLimit,
    toCountryCode: crossBorder.toCountryCode,
  })
)(SendAmountField)
